import React, { useEffect } from 'react';
import {
  Container, Col, Row, InputGroup, Input, Form, FormGroup, Label, FormText,
} from 'reactstrap';
import { Formik } from 'formik';
import { Info32 } from '@bphxd/ds-core-react/icons';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import ApolloClient from 'apollo-client';
import AWSAppSyncClient from 'aws-appsync';
import withClient from '../../hoc/withClient';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import './_findShipment.scss';
import bpBol from '../../images/bp-bol-new.png';
import bpExternalBol from '../../images/bp-external-bol.png';
import { getShipmentDetails } from '../../api/graphQl/Shipment';
import { getShipToAddresses } from '../../api/graphQl/ShipTo';
import { isShipToNameClosed } from '../../helpers/shipTo';
import { useAppContext } from '../../state/GlobalState';
import {
  initialValues, FindShipmentSchema, nonExistingShipmentMsg,
  offlineShipmentNotAvailableMessage,
  missingGetShipmentDetailsErrorPattern,
  closedShipToNameMsg,
} from './validation';
import { getDeliveryDropDetails } from '../../api/graphQl/DropDetails';
import { goButtonClickHandler, errorHandler } from '../../analytics/events';
import pageTitleConstant from '../../analytics/constantPageTitle';
import NetworkSpeedChecker from '../../components/NetworkSpeedChecker/NetworkSpeedChecker';
import { getVirtualTerminalNameById } from '../../helpers/terminals';
import { statuses } from '../../helpers/shipmentStatus';
import ShipmentLookupPanel from './ShipmentLookupPanel';
import { useOnlineEffect } from '../../helpers/openOrders';
import { getFeatureConfig } from '../../api/graphQl/Signature';
import {
  resolveSignatureValidationEnabled,
  resolveBolUploadEnabled,
  FEATURE_CONFIG,
} from '../../helpers/signatureHelper';

const FindShipment = ({ graphClient }) => {
  const history = useHistory();
  const {
    carrierId, setPlantId, handleGraphApiError, setAddresses, setShipmentId, setVirtualTerminal, setIsDPTerminal, setTripType, setIsBolUploadOptional,
  } = useAppContext();
  
  const renderShipmentError = (message) => {
    if (message !== nonExistingShipmentMsg) return message;

    return (
      <>
        {message} <Link to="/open-order">Click here</Link> to create an open order.
      </>
    );
  };

  const fetchSignatureFeatureConfig = async () => {
    if (!navigator.onLine) {
      return;
    }
    try {
      const response = await getFeatureConfig(graphClient, FEATURE_CONFIG.SIGNATURE_PAD);
      const enabled = resolveSignatureValidationEnabled(
        response?.data?.getFeatureConfig?.Config,
        carrierId,
      );
      localStorage.setItem('signatureValidationEnabled', enabled);
    } catch (error) {
      handleGraphApiError(error);
    }
  };

  const fetchBolUploadFeatureConfig = async () => {
    if (!navigator.onLine) {
      return;
    }
    try {
      const response = await getFeatureConfig(graphClient, FEATURE_CONFIG.BOL_UPLOAD);
      const enabled = resolveBolUploadEnabled(
        response?.data?.getFeatureConfig?.Config,
        carrierId,
      );
      localStorage.setItem('bolUploadEnabled', enabled);
    } catch (error) {
      handleGraphApiError(error);
    }
  };

  useOnlineEffect(fetchSignatureFeatureConfig);
  useOnlineEffect(fetchBolUploadFeatureConfig);

  useEffect(() => {
    fetchSignatureFeatureConfig();
    fetchBolUploadFeatureConfig();
  }, [graphClient, carrierId]);

  // To enable offline capabilities and limit the pages where the user needs to be offline,
  // we are pre-fetching drop details that are already associated with this shipment.
  // As data flow in this app is one-directional, in theory, this step is not required,
  // however, if the user clears their local storage, pre-fetch of drop details will make sure
  // that server DB state is correctly represented.
  // that server DB state is correctly represented.
  const fetchDropDetails = (shipmentDetails) => {
    shipmentDetails.forEach(async (shipmentDetail) => {
      getDeliveryDropDetails(graphClient, shipmentDetail);
    });
  };

  const apiGetShipTo = async (plantId) => {
    try {
      const response = await getShipToAddresses(
        graphClient,
        carrierId,
        plantId,
      );
      const shipTos = response?.data?.getShipToAddresses?.ShipTo;
      setAddresses(shipTos);
      return shipTos;
    } catch (error) {
      handleGraphApiError(error);
      return [];
    }
  };

  const onSubmit = async (values, formikBag) => {
    const {
      setFieldError,
      setStatus,
      setSubmitting,
    } = formikBag;
    const shipmentHeaderID = values.ShipmentHeaderID;

    // Call API to validate that the shipment exists
    try {
      goButtonClickHandler(pageTitleConstant.lookUpShipment);

      const shDetails = await getShipmentDetails(graphClient, shipmentHeaderID, carrierId);

      if (shDetails?.data?.getShipmentDetails) {
        if (shDetails?.data?.getShipmentDetails.length > 0) {
          // If shipment exists - render Shipment Details page
          setShipmentId(shipmentHeaderID);
          setPlantId(shDetails.data.getShipmentDetails[0].SAPPlantId);
          let isDistributorsOrder = shDetails.data.getShipmentDetails[0].IsDistributorsOrder !== undefined ? shDetails.data.getShipmentDetails[0].IsDistributorsOrder : false;
          setIsDPTerminal(isDistributorsOrder);
          let tripType = shDetails.data.getShipmentDetails[0].TripType !== undefined ? shDetails.data.getShipmentDetails[0].TripType : '';          
          setTripType(tripType);
          getVirtualTerminalNameById(shDetails.data.getShipmentDetails[0].SAPPlantId) !== null ? setVirtualTerminal(true) : setVirtualTerminal(false);
          fetchDropDetails(shDetails.data.getShipmentDetails, shipmentHeaderID);
          const shipToAddresses = await apiGetShipTo(shDetails.data.getShipmentDetails[0].SAPPlantId);
          const shipmentShipToNumber = shDetails.data.getShipmentDetails[0].ShipmentAddress?.ShipToNumber;
          const shipmentStatus = shDetails.data.getShipmentDetails[0].Status;
          let bolUploadOptional = shDetails.data.getShipmentDetails[0].IsBolEnabled !== undefined ? shDetails.data.getShipmentDetails[0].IsBolEnabled : true;
          setIsBolUploadOptional(bolUploadOptional);
          const shipToClosedStatus = shipToAddresses?.find((address) => (
            address.ShipToNumber === shipmentShipToNumber
            && shipmentStatus === statuses.Loaded
            && isShipToNameClosed(address.ShipToName)
          ));
          
          // If the ship to name is closed - show validation error         
          if (shipToClosedStatus) {
            setFieldError('ShipmentHeaderID', closedShipToNameMsg);
            setStatus({ success: false });
            setSubmitting(false);

            errorHandler(pageTitleConstant.lookUpShipment);
          } else {
            history.push('/shipment-details', { fromFindShipment: true });
          }
        } else {
          // If the shipment doesn't exist - show validation error
          setFieldError('ShipmentHeaderID', nonExistingShipmentMsg);
          setStatus({ success: false });
          setSubmitting(false);

          errorHandler(pageTitleConstant.lookUpShipment);
        }
      }
    } catch (error) {
      // Any error that has a statusCode means that is was a failure at
      // the server. If this is the case then pass that message to the context to handle.
      if (error?.networkError?.statusCode) {
        handleGraphApiError(error);
      }
      // Handle other network errors here
      // for example when we cant find the response for a query
      if (error?.networkError?.message?.includes(missingGetShipmentDetailsErrorPattern)) {
        // If the shipment doesn't exist - show validation error
        setFieldError('ShipmentHeaderID', offlineShipmentNotAvailableMessage);
        setStatus({ success: false });
        setSubmitting(false);

        errorHandler(pageTitleConstant.lookUpShipment);
      }
    }
  };

  return (
    <Container fluid className="lookup-page" data-testid="find-shipment-container">
      <NetworkSpeedChecker />
      <Formik
        initialValues={initialValues}
        validationSchema={FindShipmentSchema}
        onSubmit={(values, formikBag) => onSubmit(values, formikBag)}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          isSubmitting,
          values,
          errors,
        }) => (
          <Form noValidate onSubmit={handleSubmit} data-testid="find-shipment-form">
            <ShipmentLookupPanel activeTab="delivery">
              <Row className="shipment-lookup-fields">
                <Col xs="12">
                  <FormGroup>
                    <Label className="shipment-header" for="ShipmentHeaderID">Enter Shipment Number/External BOL/Load ID</Label>
                    <InputGroup>
                      <Input
                        name="ShipmentHeaderID"
                        data-testid="shipment-header-id-field"
                        value={values.ShipmentHeaderID}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={Boolean(errors.ShipmentHeaderID)}
                        autoComplete="off"
                        type="tel"
                        placeholder="e.g. 1234567890, BOL123, or Load ID"
                        className="input-colour"
                      />
                    </InputGroup>
                    {errors.ShipmentHeaderID
                      ? <FormText color="error">{errors.ShipmentHeaderID}</FormText>
                      : null}
                  </FormGroup>
                </Col>
              </Row>
              <div className="shipment-lookup-help">
                <Info32 aria-hidden="true" />
                <span>Enter the shipment number shown on the Bill of Lading or External BOL number/Load ID Addon.</span>
              </div>
              <PrimaryButton
                loading={isSubmitting}
                disabled={isSubmitting || !values.ShipmentHeaderID}
                className="shipment-lookup-submit"
                data-testid="submit-button"
                type="Submit"
                label="Go"
              />
            </ShipmentLookupPanel>
            <Row>
              <Col sm="10" className="mx-auto">
                <div className="line" />
              </Col>
            </Row>
            <Row>
              <Col sm="10" className="mx-auto">
                <Label className="graphic-help univers">Where to find shipment number / Load ID</Label>
              </Col>
              <Col sm="10" className="mx-auto text-col mb-5 mt-4 d-flex align-items-center">
                <span>
                  <Info32 className="info-icon" />
                </span>
                <span className="text">
                  The Load ID Addon is listed at the top of the Load Instruction in the reference details box.                
                  <br />
                  The shipment number is listed right below the Load ID Addon
                  on the Bill of Lading.
                </span>
              </Col>
              <Col sm="10" className="mx-auto">
                <img className="graphic img-fluid" alt="BOL Graphic" src={bpBol} />
              </Col>
            </Row>
            <Row>
              <Col sm="10" className="mx-auto">
                <div className="line" />
              </Col>
            </Row>
            <Row>
              <Col sm="10" className="mx-auto">
                <Label className="graphic-help univers">Where to find External BOL</Label>
              </Col>
              <Col sm="10" className="mx-auto text-col mb-5 mt-4 d-flex align-items-center">
                <span>
                  <Info32 className="info-icon" />
                </span>
                <span className="text">
                  The External BOL is listed on the Bill of Lading in the reference details box.                                    
                </span>
              </Col>
              <Col sm="10" className="mx-auto">
                <img className="graphic img-fluid" alt="BOL Graphic" src={bpExternalBol} />
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

/**
 * We use oneOfType because
 * the application uses AppSyncClient
 * the tests/jest provide ApolloClient as we use the <MockProvider/>
 */
FindShipment.propTypes = {
  graphClient: PropTypes.oneOfType([
    PropTypes.instanceOf(AWSAppSyncClient).isRequired,
    PropTypes.instanceOf(ApolloClient).isRequired,
  ]).isRequired,
};

// withClient will provide the client in props
export default withClient(FindShipment);
